
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton, TextField, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Tooltip, InputAdornment,
} from '@mui/material';
import { FixedSizeList } from 'react-window';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LabelIcon from '@mui/icons-material/Label';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import BillPrint from './Bill';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toWords } from 'number-to-words';
import Logo from './../Logo/QR.png';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    accent: '#2dd4bf', accentDark: '#14b8a6',
    accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
    bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
    text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
    danger: '#ef4444', dangerDark: '#dc2626',
};

const COLS = [
    { label: '#', flex: '0 0 44px', align: 'left' },
    { label: 'Bill Date', flex: '0 0 90px', align: 'left' },
    { label: 'Bill No', flex: '1 1 0', align: 'left' },
    { label: 'Customer', flex: '2 1 0', align: 'left' },
    { label: 'Amount', flex: '0 0 90px', align: 'right' },
    { label: 'CGST', flex: '0 0 80px', align: 'right' },
    { label: 'SGST', flex: '0 0 80px', align: 'right' },
    { label: 'IGST', flex: '0 0 80px', align: 'right' },
    { label: 'Total', flex: '0 0 90px', align: 'right' },
    { label: 'Qty', flex: '0 0 60px', align: 'right' },
    { label: '', flex: '0 0 140px', align: 'right' },
];

const ROW_HEIGHT = 36;
const LIST_HEIGHT = 500;

const rowBase = {
    display: 'flex', alignItems: 'center', width: '100%',
    boxSizing: 'border-box', borderBottom: `1px solid ${C.cardBorder}`,
};

function ColCell({ col, children, muted, bold }) {
    return (
        <div style={{
            flex: col.flex, minWidth: 0, padding: '0 8px', fontSize: '12px',
            color: muted ? C.muted : C.text, fontWeight: bold ? 600 : 400,
            textAlign: col.align, overflow: 'hidden', whiteSpace: 'nowrap',
            textOverflow: 'ellipsis', boxSizing: 'border-box',
        }}>
            {children}
        </div>
    );
}

const SaleRow = React.memo(({ index, style, data: listData }) => {
    const { items, onDelete, onActivate, onPrint, onA4Print, hasEdit, hasDelete, hasCreate, formatDate } = listData;
    const item = items[index];
    const isEven = index % 2 === 0;

    return (
        <div
            style={{
                ...style, ...rowBase,
                backgroundColor: isEven ? C.card : '#f7fdfc',
                transition: 'background-color 0.15s',
                opacity: item.IsActive ? 1 : 0.55,
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.accentDim; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = isEven ? C.card : '#f7fdfc'; }}
        >
            <ColCell col={COLS[0]} muted>{index + 1}</ColCell>
            <ColCell col={COLS[1]}>{formatDate(item.BillDate)}</ColCell>
            <ColCell col={COLS[2]} bold>{item.BillNumber}/{item.FinYear}</ColCell>
            <ColCell col={COLS[3]}>{item.CustomerName}</ColCell>
            <ColCell col={COLS[4]}>{item.TaxableAmount}</ColCell>
            <ColCell col={COLS[5]}>{item.CGSTAmount}</ColCell>
            <ColCell col={COLS[6]}>{item.SGSTAmount}</ColCell>
            <ColCell col={COLS[7]}>{item.IGSTAmount}</ColCell>
            <ColCell col={COLS[8]}><strong style={{ color: '#0D47A1' }}>{item.GrandTotal}</strong></ColCell>
            <ColCell col={COLS[9]}>{item.TotalQuantity}</ColCell>
            <ColCell col={COLS[10]}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2px', alignItems: 'center' }}>
                    <Tooltip title="Details" arrow>
                        <Link to={`/SaleDetails/${item.Id}`}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#f59e0b', textDecoration: 'none' }}>
                            <InfoIcon style={{ fontSize: 15 }} />
                        </Link>
                    </Tooltip>
                    {hasCreate && item.IsActive && (
                        <Tooltip title="Sale Return" arrow>
                            <Link to={`/SaleReturnCreate/${item.Id}`}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#3b82f6', textDecoration: 'none' }}>
                                <AssignmentReturnIcon style={{ fontSize: 15 }} />
                            </Link>
                        </Tooltip>
                    )}
                    {hasCreate && item.IsActive && (
                        <Tooltip title="Print" arrow>
                            <button onClick={() => onPrint(item.Id)}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                <PrintIcon style={{ fontSize: 15 }} />
                            </button>
                        </Tooltip>
                    )}
                    {hasCreate && item.IsActive && (
                        <Tooltip title="A4 Print" arrow>
                            <button onClick={() => onA4Print(item.Id)}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                <PictureAsPdfIcon style={{ fontSize: 15 }} />
                            </button>
                        </Tooltip>
                    )}
                    {hasEdit && item.IsActive && (
                        <Tooltip title="Edit" arrow>
                            <Link to={`/SaleEdit/${item.Id}`}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#7c3aed', textDecoration: 'none' }}>
                                <EditIcon style={{ fontSize: 15 }} />
                            </Link>
                        </Tooltip>
                    )}
                    {hasDelete && item.IsActive && (
                        <Tooltip title="Delete" arrow>
                            <button onClick={() => onDelete(item.Id)}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: C.danger, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                <DeleteIcon style={{ fontSize: 15 }} />
                            </button>
                        </Tooltip>
                    )}
                    {!item.IsActive && (
                        <Tooltip title="Activate" arrow>
                            <button onClick={() => onActivate(item.Id)}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                <CheckCircleIcon style={{ fontSize: 15 }} />
                            </button>
                        </Tooltip>
                    )}
                </div>
            </ColCell>
        </div>
    );
});

function SaleIndex() {
    const history = useHistory();
    const location = useLocation();
    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();

    const hasCreate = permissions.includes('Create Transaction');
    const hasEdit = permissions.includes('Edit Transaction');
    const hasDelete = permissions.includes('Delete Transaction');

    // Keep legacy aliases for backward compat
    const hasCreateMasterPermission = hasCreate;
    const hasEditMasterPermission = hasEdit;
    const hasDeleteMasterPermission = hasDelete;

    const getFormattedCurrentDate = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const getFormattedPreviousDate = () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const getFormattedStartDate = () => {
        const d = new Date();
        d.setDate(1);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
    };

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(getFormattedPreviousDate);
    const [toDate, setToDate] = useState(getFormattedCurrentDate);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');

    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const debounceRef = useRef(null);
    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchQuery(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedQuery(val), 200);
    };

    const branchId = 0;

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';
        if (shouldReload) { localStorage.removeItem('shouldReloadCreatePage'); window.location.reload(); return; }
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, companyId, token]);

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';
        const from = location.state?.from;
        if (['/SaleCreate', '/SaleEdit', '/SaleDetails'].includes(from) && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        } else {
            setFromDate(getFormattedPreviousDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);

    const fetchtableData = async () => {
        setLoading(true);
        setData([]);
        try {
            const response = await fetchData('SaleApi', `/SaleDetails/${companyId}/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Sale Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        if (!debouncedQuery.trim()) return data;
        const q = debouncedQuery.toLowerCase();
        return data.filter(item =>
            item.BillNumber?.toString().toLowerCase().includes(q) ||
            item.CustomerName?.toString().toLowerCase().includes(q) ||
            item.FinYear?.toString().toLowerCase().includes(q)
        );
    }, [data, debouncedQuery]);

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    }

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const handleDelete = async () => {
        try {
            await deleteData('SaleApi', `${deleteItemId}`, token);
            setDeleteItemId(null);
            setConfirmDialogOpen(false);
            fetchtableData();
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    const handleActivate = (itemId) => {
        activateData('SaleApi', `${itemId}`, token)
            .then(() => fetchtableData())
            .catch((error) => console.error('Error activating record:', error));
    };

    const openConfirmDialog = (itemId) => { setDeleteItemId(itemId); setConfirmDialogOpen(true); };
    const closeConfirmDialog = useCallback(() => { setDeleteItemId(null); setConfirmDialogOpen(false); }, []);

    const handleA4Print = async (SALEID) => {
        try {
            const response = await fetchData('SaleApi', `/SalePrint/${SALEID}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };


    const handlePrint = async (SALEID) => {
        try {
            // ------------------ FETCH DATA ------------------
            const response = await fetchData('SaleApi', `/SalePrint/${SALEID}`, token);
            const labelData = response.response_data;

            if (!labelData?.SalePrint || !labelData?.SalePrintItems) {
                throw new Error("Invalid invoice data.");
            }

            const header = labelData.SalePrint;
            const items = labelData.SalePrintItems;

            // ------------------ INITIALIZE PDF ------------------
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [80, 280] // POS roll size
            });

            const margin = { top: 5, left: 0.8, right: 0.8 };
            const pageWidth = doc.internal.pageSize.getWidth();
            const usableWidth = pageWidth - margin.left - margin.right;
            let y = margin.top;

            const yStartOuter = margin.top - 1;

            // ------------------ HEADER ------------------
            y += 2;
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`GSTIN : ${header.CompanyGSTNumber || ''}`, margin.left + 1, y);
            doc.text(`Ph : ${header.CompanyContactNumber1 || ''}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 6;

            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(header.CompanyName || '', pageWidth / 2, y, { align: 'center' });
            y += 6;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            const addrLines = doc.splitTextToSize(header.CompanyAddressLine || '', usableWidth);
            addrLines.forEach(line => {
                doc.text(line, pageWidth / 2, y, { align: 'center' });
                y += 3;
            });

            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text("Bill of Supply", pageWidth / 2, y, { align: 'center' });
            y += 3;

            // ------------------ BILL INFO + CUSTOMER ------------------
            const boxYStart = y;
            y += 3;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);

            const invNo = header.InvoiceNo || [header.BillNumber, header.FinYear].filter(Boolean).join('/');
            const billDate = header.BillDate ? new Date(header.BillDate).toLocaleDateString('en-GB') : '';
            const billTime = header.BillTime;

            doc.text(`Bill No: ${invNo}`, margin.left + 1, y);
            doc.text(`Date: ${billDate}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 3;
            doc.text(`Time: ${billTime}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 4;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text("Customer Details:", margin.left + 1, y);
            y += 3;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.text(`Name: ${header.CustomerName || ''}`, margin.left + 1, y);
            y += 3;

            if (header.CustomerAddress) {
                doc.text(`Address: ${header.CustomerAddress}`, margin.left + 1, y);
                y += 3;
            }

            if (header.CustomerContactNumber) {
                doc.text(`Contact: ${header.CustomerContactNumber}`, margin.left + 1, y);
                y += 3;
            }

            const boxYEnd = y + 1;
            doc.setLineWidth(0.1);
            doc.rect(0.8, boxYStart - 1, pageWidth - 1.6, boxYEnd - boxYStart + 1);
            y = boxYEnd;

            // ------------------ ITEMS TABLE ------------------
            const filteredItems = items.filter(i => (i.Quantity ?? 0) > 0);
            const rows = filteredItems.map((i, idx) => [
                idx + 1,
                i.ItemName || "",
                (i.Quantity ?? 0).toString(),
                (i.Rate ?? 0).toFixed(2),
                (i.DiscountAmount ?? 0).toFixed(2),
                (i.TotalAmount ?? 0).toFixed(2),
            ]);


            autoTable(doc, {
                startY: y,
                margin: { left: margin.left, right: margin.right },
                head: [["Sr", "Product", "Qty", "Rate", "Disc", "Amount"]],
                body: rows,
                theme: "grid",
                styles: {
                    fontSize: 8,
                    cellPadding: 1,
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1,
                    textColor: [0, 0, 0],
                    fontStyle: "bold", // ✅ bold for all item rows
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1,
                    fontStyle: "bold", // ✅ bold for header too
                },
                columnStyles: {
                    0: { halign: 'left' },
                    1: { halign: 'left' },
                    2: { halign: 'right' },
                    3: { halign: 'right' },
                    4: { halign: 'right' },
                    5: { halign: 'right' },
                },
            });


            y = doc.lastAutoTable.finalY + 6;

            // ------------------ TOTALS (Right Side Aligned, Left Labels) ------------------
            const leftX = margin.left + 45;
            const rightX = pageWidth - margin.right - 1.5;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);

            // Taxable Amount
            doc.text(`Taxable Amt`, leftX, y);
            doc.text(`${(Number(header.TaxableAmount) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // CGST Amount
            doc.text(`CGST Amt`, leftX, y);
            doc.text(`${(Number(header.CGSTAmount) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // SGST Amount
            doc.text(`SGST Amt`, leftX, y);
            doc.text(`${(Number(header.SGSTAmount) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // Round Off
            doc.text(`Round Off`, leftX, y);
            doc.text(`${(Number(header.RoundOff) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // Total Amount (bold)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text(`Total Amt`, leftX, y);
            doc.text(`${(Number(header.GrandTotal) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;
            doc.setFont('helvetica', 'normal'); // reset font

            // ------------------ AMOUNT IN WORDS ------------------
            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;

            const grandTotal = Number(header.GrandTotal) || 0;
            const numberToWords = (num) => {
                const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
                const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                const inWords = (n) => {
                    if (n < 20) return a[n];
                    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
                    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + inWords(n % 100) : '');
                    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
                    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
                    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
                };
                const parts = num.toFixed(2).split('.');
                let words = inWords(Number(parts[0])) + ' Rupees';
                if (Number(parts[1]) > 0) words += ' and ' + inWords(Number(parts[1])) + ' Paise';
                return words + ' Only';
            };

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.text(`${numberToWords(grandTotal)}`, margin.left + 1, y);
            y += 2;

            // ------------------ TERMS & CONDITIONS ------------------
            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;

            doc.setFont('helvetica', 'bold');
            doc.text(" ", margin.left + 1, y);
            y += 2;

            // ------------------ QR + Tax Breakdown (side by side) ------------------
            const qrBoxWidth = pageWidth * 0.3; // ~30% width for QR
            const qrBoxHeight = 25;
            const tableStartX = qrBoxWidth + 5; // leave small gap between QR and table
            let sectionY = y;

            // --- QR ---
            try {
                doc.addImage(
                    Logo, "PNG",
                    margin.left + 1,
                    sectionY,
                    qrBoxWidth, qrBoxHeight
                );
            } catch (error) {
                console.warn("QR image missing", error);
                doc.setFontSize(6);
                doc.text("QR IMAGE MISSING", margin.left + 1 + qrBoxWidth / 2, sectionY + qrBoxHeight / 2, { align: 'center' });
            }

            // --- Tax Breakdown Table ---
            const filteredItem = items.filter(i => (i.Quantity ?? 0) > 0);
            const taxGroups = {};
            filteredItem.forEach(item => {
                const gst = item.GSTRate ?? 0;
                if (!taxGroups[gst]) taxGroups[gst] = { amount: 0, cgst: 0, sgst: 0 };
                taxGroups[gst].amount += item.TaxableAmount ?? 0;
                taxGroups[gst].cgst += item.CGSTAmount ?? 0;
                taxGroups[gst].sgst += item.SGSTAmount ?? 0;
            });

            const taxBreakdownRows = Object.entries(taxGroups).map(([percent, vals]) => ([
                percent,
                vals.amount.toFixed(2),
                vals.cgst.toFixed(2),
                vals.sgst.toFixed(2)
            ]));

            const totalRow = taxBreakdownRows.reduce((acc, row) => {
                for (let i = 1; i <= 3; i++) acc[i] += parseFloat(row[i]);
                return acc;
            }, ['Total', 0, 0, 0]).map((v, i) => i === 0 ? v : v.toFixed(2));

            taxBreakdownRows.push(totalRow);

            // Table layout
            const colWidths = [8, 13, 13, 13];
            const colTitles = ['%', 'Amount', 'CGST', 'SGST'];

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text('Tax Breakdown', tableStartX, sectionY);

            let tableY = sectionY + 2;

            // Header
            let colX = tableStartX;
            colTitles.forEach((title, i) => {
                doc.rect(colX, tableY, colWidths[i], 5);
                doc.text(title, colX + colWidths[i] / 2, tableY + 3.2, { align: 'center' });
                colX += colWidths[i];
            });

            // Body
            doc.setFont('helvetica', 'normal');
            let currentY = tableY + 5;
            taxBreakdownRows.forEach(row => {
                let rowX = tableStartX;
                row.forEach((cell, i) => {
                    doc.rect(rowX, currentY, colWidths[i], 5);
                    doc.text(cell.toString(), rowX + colWidths[i] / 2, currentY + 3.2, { align: 'center' });
                    rowX += colWidths[i];
                });
                currentY += 5;
            });

            // Update y for next section
            y = Math.max(sectionY + qrBoxHeight, currentY) + 6;


            // ------------------ FOOTER ------------------
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.text("THANK YOU - VISIT AGAIN", pageWidth / 2, y, { align: 'center' });

            const yEndOuter = y + 2;
            doc.setLineWidth(0.1);
            doc.rect(0.5, yStartOuter, pageWidth - 1, yEndOuter - yStartOuter);

            // ------------------ PRINT ------------------
            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, '_blank');
            if (win) win.onload = () => {
                win.focus();
                win.print();
            };

        } catch (err) {
            console.error("Error generating invoice:", err);
        }
    };



    const itemData = useMemo(() => ({
        items: filteredData,
        onDelete: (id) => { setDeleteItemId(id); setConfirmDialogOpen(true); },
        onActivate: handleActivate,
        onPrint: handlePrint,
        onA4Print: handleA4Print,
        hasCreate,
        hasEdit,
        hasDelete,
        formatDate,
    }), [filteredData, hasCreate, hasEdit, hasDelete]);

    const btnSecondary = {
        fontSize: '11px', fontWeight: 600, textTransform: 'none',
        color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
        borderRadius: '7px', px: 1.4, py: 0.5,
        '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
    };
    const btnDanger = {
        fontSize: '11px', fontWeight: 700, textTransform: 'none',
        bgcolor: C.danger, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5,
        '&:hover': { bgcolor: C.dangerDark },
    };

    return (
        <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

            {/* ── Page header ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <PointOfSaleIcon sx={{ fontSize: 16, color: C.accent }} />
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
                        Sale
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Date range */}
                    <TextField type="date" size="small" value={fromDate} onChange={handleFromDateChange}
                        inputProps={{ style: { fontSize: '11px', padding: '4px 6px' } }}
                        sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: '7px', height: '28px', bgcolor: C.card, '& fieldset': { borderColor: C.cardBorder } } }} />
                    <TextField type="date" size="small" value={toDate} onChange={handleToDateChange}
                        inputProps={{ style: { fontSize: '11px', padding: '4px 6px' } }}
                        sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: '7px', height: '28px', bgcolor: C.card, '& fieldset': { borderColor: C.cardBorder } } }} />

                    {/* Search */}
                    <TextField size="small" placeholder="Search bill / customer…"
                        value={searchQuery} onChange={handleSearch}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: C.muted }} /></InputAdornment> }}
                        sx={{
                            width: 200,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px', fontSize: '12px', height: '28px', bgcolor: C.card,
                                '& fieldset': { borderColor: C.cardBorder },
                                '&:hover fieldset': { borderColor: C.accentBorder },
                                '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
                            },
                        }} />

                    {/* Refresh */}
                    <Tooltip title="Refresh" arrow>
                        <button onClick={fetchtableData} disabled={loading}
                            style={{
                                height: '28px', padding: '0 12px', borderRadius: '7px',
                                border: `1px solid ${C.cardBorder}`, backgroundColor: C.card,
                                color: C.muted, fontWeight: 600, fontSize: '11px',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                boxShadow: C.shadow, fontFamily: 'inherit',
                            }}>
                            {loading
                                ? <CircularProgress size={11} sx={{ color: C.muted }} />
                                : <RefreshIcon sx={{ fontSize: 13 }} />}
                            Refresh
                        </button>
                    </Tooltip>
                    {hasCreate && (
                        <Link to="/SaleCreate"
                            style={{
                                height: '28px', padding: '0 14px', borderRadius: '7px',
                                border: 'none', backgroundColor: C.accent,
                                color: '#fff', fontWeight: 700, fontSize: '11px',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                boxShadow: C.shadow, textDecoration: 'none', fontFamily: 'inherit',
                            }}>
                            <AddIcon sx={{ fontSize: 13 }} />
                            Create
                        </Link>
                    )}

                </Box>
            </Box>

            {/* ── Table card ── */}
            <Box sx={{ bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, overflow: 'hidden' }}>
                {/* Card title bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`, bgcolor: `${C.accent}0d` }}>
                    <LabelIcon sx={{ fontSize: 15, color: C.accent }} />
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        Sale List
                    </Typography>
                    <Box sx={{ ml: 'auto' }}>
                        <Typography sx={{ fontSize: '10px', color: C.muted }}>
                            {filteredData.length} of {data.length} record{data.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} sx={{ color: C.accent }} />
                    </Box>
                ) : (
                    <div style={{ width: '100%' }}>
                        {/* Column header */}
                        <div style={{ ...rowBase, backgroundColor: '#f8fafa', borderBottom: `2px solid ${C.cardBorder}`, width: '100%' }}>
                            {COLS.map((col, i) => (
                                <div key={i} style={{
                                    flex: col.flex, minWidth: 0, padding: '7px 8px',
                                    fontSize: '10px', fontWeight: 700, color: C.muted,
                                    textTransform: 'uppercase', letterSpacing: '0.5px',
                                    textAlign: col.align, boxSizing: 'border-box',
                                }}>
                                    {col.label}
                                </div>
                            ))}
                        </div>

                        {filteredData.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 5, color: C.muted, fontSize: '12px' }}>
                                {data.length === 0 ? 'Select a date range and click Refresh' : 'No records found'}
                            </Box>
                        ) : (
                            <FixedSizeList
                                height={Math.min(LIST_HEIGHT, filteredData.length * ROW_HEIGHT)}
                                itemCount={filteredData.length}
                                itemSize={ROW_HEIGHT}
                                width="100%"
                                itemData={itemData}
                                overscanCount={8}
                            >
                                {SaleRow}
                            </FixedSizeList>
                        )}
                    </div>
                )}
            </Box>

            {/* ── Delete Confirm Dialog ── */}
            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
                <DialogTitle sx={{ fontSize: '13px', fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.cardBorder}`, pb: 0.5 }}>
                    Delete Sale?
                </DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <DialogContentText sx={{ fontSize: '12px', color: C.muted }}>
                        Are you sure you want to delete this sale record? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
                    <Button onClick={closeConfirmDialog} sx={btnSecondary}>Cancel</Button>
                    <Button onClick={handleDelete} sx={btnDanger} startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default SaleIndex;



